import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/common/EmptyState";
import { Flag, ShieldCheck, ArrowRightLeft, XCircle, CheckCircle2, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

const TABS = [["needs_review", "Open"], ["resolved", "Resolved"], ["all", "All"]];

function fmt(d) { return d ? new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—"; }

export default function NeedsReviewQueue() {
  const [status, setStatus] = useState("needs_review");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/referral-reviews", { params: { status } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/referral-reviews/${id}/resolve`, { action, note: notes[id] || "" });
      toast.success(action === "reassign" ? "Attribution reassigned" : action === "keep_existing" ? "Existing attribution kept" : "Review dismissed");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-5" data-testid="needs-review-queue">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
            <span className="rounded-lg bg-amber-100 text-amber-700 p-1.5"><Flag className="w-5 h-5" /></span>
            Referral Needs-Review
          </h1>
          <p className="text-sm text-slate-500 mt-1">Resolve referral attribution conflicts. The existing authoritative referral is preserved until you decide.</p>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="review-tabs">
          {TABS.map(([k, l]) => (
            <button key={k} data-testid={`review-tab-${k}`} onClick={() => setStatus(k)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${status === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading ? <p className="text-sm text-slate-400">Loading…</p>
        : !data?.items?.length ? (
          <EmptyState icon={CheckCircle2} title="Nothing to review" description="There are no referral attribution conflicts in this view." />
        ) : (
          <div className="space-y-4">
            {data.items.map((r) => {
              const open = r.status === "needs_review";
              return (
                <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`review-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{r.person.name}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                        {r.person.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{r.person.email}</span>}
                        {r.person.mobile && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{r.person.mobile}</span>}
                        {(r.person.city || r.person.state) && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{[r.person.city, r.person.state].filter(Boolean).join(", ")}</span>}
                        {r.person.organization_name && <span>{r.person.organization_name}</span>}
                      </div>
                      {r.person.classifications?.length > 0 && (
                        <div className="flex gap-1.5 mt-2">
                          {r.person.classifications.map((c) => <span key={c} className="text-[10px] font-medium uppercase tracking-wide bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{c}</span>)}
                        </div>
                      )}
                    </div>
                    <span className={`text-[11px] font-semibold rounded-full px-2.5 py-1 shrink-0 ${open ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}>
                      {open ? "Attribution Conflict" : `Resolved · ${r.resolution || ""}`}
                    </span>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3" data-testid={`review-existing-${r.id}`}>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" />Current authoritative referrer</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">{r.existing_referrer?.name || "Unattributed"}</p>
                      <p className="text-xs text-slate-500">{r.existing_referrer?.source || "—"}{r.existing_referrer?.occurred_at ? ` · ${fmt(r.existing_referrer.occurred_at)}` : ""}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`review-attempted-${r.id}`}>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><ArrowRightLeft className="w-3.5 h-3.5" />Attempted by</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">{r.attempted_by?.name || "Unknown partner"}</p>
                      <p className="text-xs text-slate-500">Submitted {fmt(r.created_at)}{r.submitted?.email ? ` · ${r.submitted.email}` : ""}</p>
                    </div>
                  </div>

                  {open ? (
                    <div className="mt-4 space-y-2">
                      <Textarea rows={2} placeholder="Optional resolution note (recorded in the audit log)"
                        value={notes[r.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                        data-testid={`review-note-${r.id}`} />
                      <div className="flex flex-wrap gap-2 justify-end">
                        <Button variant="outline" size="sm" disabled={!!busy} onClick={() => resolve(r.id, "dismiss")} data-testid={`review-dismiss-${r.id}`}>
                          <XCircle className="w-4 h-4 mr-1.5" />Dismiss
                        </Button>
                        <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={!!busy} onClick={() => resolve(r.id, "keep_existing")} data-testid={`review-keep-${r.id}`}>
                          <ShieldCheck className="w-4 h-4 mr-1.5" />Keep Existing
                        </Button>
                        <Button size="sm" className="bg-brand hover:bg-brand-strong text-white" disabled={!!busy || !r.attempted_by?.user_id} onClick={() => resolve(r.id, "reassign")} data-testid={`review-reassign-${r.id}`}>
                          <ArrowRightLeft className="w-4 h-4 mr-1.5" />Assign to {r.attempted_by?.name?.split(" ")[0] || "attempter"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 mt-3">Resolved by {r.resolved_by_name || "—"} on {fmt(r.resolved_at)}{r.resolution_note ? ` — “${r.resolution_note}”` : ""}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
